import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaDownload, FaLock } from 'react-icons/fa';
import { Carousel } from 'react-responsive-carousel';

import { axiosInstance } from '../../config/axios';
import { LoadingGif, LoadingMessage } from '../Shared/CommonStyles';

import * as S from './MediaLightbox.styles';

interface MediaItem {
	_id: string;
	path: string;
	type: 'image' | 'video';
	thumbnail_path: string;
	compressed_path?: string;
	original_file_size?: number;
	file_size: number;
	width: number;
	height: number;
	rotation?: number;
	thumbnail_width: number;
	thumbnail_height: number;
	upload_date: string;
	album: string;
	isSecret?: boolean;
	albumName?: string;
	unitat?: string;
}

interface GalleryImage {
	_id?: string;
	src: string;
	width: number;
	height: number;
	caption?: string;
}

interface MediaLightboxProps {
	isOpen: boolean;
	currentImage: number | null;
	fullImages: GalleryImage[];
	mediaItems: MediaItem[];
	onClose: () => void;
	onChangeImage: (newIndex: number) => void;
	page: number; // Add page
	pageSize: number; // Add pageSize
	totalCount?: number; // Add totalCount
	hideAlbumName?: boolean; // Add hideAlbumName
}

const MediaLightbox: React.FC<MediaLightboxProps> = ({
	isOpen,
	currentImage,
	fullImages,
	mediaItems, // Receive mediaItems from parent
	onClose,
	onChangeImage,
	page,
	pageSize,
	totalCount,
	hideAlbumName,
}) => {
	const lightboxRef = useRef<HTMLDivElement>(null);
	const [showDownloadModal, setShowDownloadModal] = useState(false);

	// Format file size compactly: MB (unless under 100 KB, then KB)
	const formatSizeCompact = useCallback((bytes: number): string => {
		if (bytes < 0) return '?';
		if (bytes < 100 * 1024) {
			return `${Math.round(bytes / 1024)} KB`;
		}
		return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	}, []);

	// Fetch a download URL via axiosInstance (handles JWT, refresh, base URL), then navigate to it
	const downloadViaBackend = useCallback(async (itemId: string, version: 'original' | 'web') => {
		try {
			const response = await axiosInstance.get<{ url: string }>(
				`/api/galeria/gallery-items/download/${itemId}?version=${version}`
			);
			const downloadUrl = response.data.url;
			if (downloadUrl) {
				const a = document.createElement('a');
				a.href = downloadUrl;
				a.style.display = 'none';
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
			}
		} catch (err) {
			console.error('Error descarregant fitxer:', err);
		}
	}, []);

	// Handler to download the current media item
	const handleDownloadClick = useCallback(() => {
		if (currentImage === null || !mediaItems[currentImage]) return;
		const item = mediaItems[currentImage];

		if (item.type === 'video') {
			downloadViaBackend(item._id, 'original');
			return;
		}

		// For images: only show the modal if a compressed version actually exists
		// (compressed_path will be a signed URL string if it does)
		if (!item.compressed_path) {
			downloadViaBackend(item._id, 'original');
			return;
		}

		// Show the modal
		setShowDownloadModal(true);
	}, [currentImage, mediaItems, downloadViaBackend]);

	// Handler to download a specific version from the modal
	const handleVersionDownload = useCallback((version: 'original' | 'web') => {
		if (currentImage === null || !mediaItems[currentImage]) return;
		downloadViaBackend(mediaItems[currentImage]._id, version);
		setShowDownloadModal(false);
	}, [currentImage, mediaItems, downloadViaBackend]);

	// Handler to detect wrap-around scenarios from carousel swipe
	// and convert them to out-of-bounds indices for page navigation
	const handleCarouselChange = (newIndex: number) => {
		const totalImages = fullImages.length;

		// Only check wrap-around if we have multiple images
		if (totalImages > 1 && currentImage !== null) {
			// Forward wrap: user swiped right at last image, carousel wrapped to 0
			if (currentImage === totalImages - 1 && newIndex === 0) {
				onChangeImage(totalImages); // Pass out-of-bounds to trigger next page
				return;
			}
			// Backward wrap: user swiped left at first image, carousel wrapped to last
			if (currentImage === 0 && newIndex === totalImages - 1) {
				onChangeImage(-1); // Pass out-of-bounds to trigger previous page
				return;
			}
		}

		// Normal navigation within page
		onChangeImage(newIndex);
	};

	// Effect to handle fullscreen on mobile
	useEffect(() => {
		const element = lightboxRef.current;
		// Use User Agent to detect mobile devices, which works in landscape mode.
		const isMobile =
			/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
				navigator.userAgent
			);

		if (isOpen && isMobile && element) {
			// Request fullscreen when lightbox opens on mobile
			if (element.requestFullscreen) {
				element.requestFullscreen().catch((err) => {
					console.error(
						`Error attempting to enable fullscreen: ${err.message} (${err.name})`
					);
				});
			}

			const handleFullscreenChange = () => {
				// If fullscreen is exited (e.g., by user pressing Esc), close the lightbox
				if (!document.fullscreenElement) {
					onClose();
				}
			};

			document.addEventListener(
				'fullscreenchange',
				handleFullscreenChange
			);

			return () => {
				document.removeEventListener(
					'fullscreenchange',
					handleFullscreenChange
				);
				// Ensure fullscreen is exited when lightbox closes normally
				if (document.fullscreenElement) {
					document.exitFullscreen();
				}
			};
		} else if (!isOpen && document.fullscreenElement) {
			// If lightbox is closed and still in fullscreen, exit fullscreen
			document.exitFullscreen();
		}
	}, [isOpen, onClose]);

	// Early return if not open or no image selected
	if (!isOpen || currentImage === null || !mediaItems) {
		return null;
	}

	// Helper to render legend content
	const renderLegend = (index: number | null, mediaItem?: MediaItem) => {
		if (index === null || !mediaItem) return null;

		const absoluteIndex = (page - 1) * pageSize + index + 1;
		const total = totalCount || mediaItems.length; // Fallback to current length if total not provided

		return (
			<S.CarouselImageLegend className="legend" onClick={(e) => e.stopPropagation()}>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: '4px',
						flex: 1,
					}}
				>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							gap: '8px',
						}}
					>
						{mediaItem?.isSecret && <FaLock title="Secret" />}
						<span>
							Foto {absoluteIndex} / {total}
						</span>
					</div>
					<div
						style={{
							fontSize: '0.85em',
							opacity: 0.9,
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						{!hideAlbumName && mediaItem.albumName && (
							<span>{mediaItem.albumName}</span>
						)}
						{mediaItem.unitat && (
							<span
								style={{
									fontSize: '0.9em',
									opacity: 0.8,
								}}
							>
								{mediaItem.unitat.toUpperCase()}
							</span>
						)}
					</div>
				</div>
				<S.DownloadButton onClick={handleDownloadClick} title={mediaItem.type === 'video' ? 'Descarregar vídeo' : 'Descarregar imatge'}>
					<FaDownload />
				</S.DownloadButton>
			</S.CarouselImageLegend>
		);
	};

	return (
		<S.CarouselBackdrop onClick={onClose} ref={lightboxRef}>
			<S.CarouselContent>
				{/* Render Custom Buttons */}
				<S.CustomPrevButton
					onClick={(e) => {
						e.stopPropagation(); // Prevent backdrop close
						if (currentImage !== null)
							onChangeImage(currentImage - 1); // Use prop handler
					}}
					aria-label="Previous Image"
				>
					{'<'}
				</S.CustomPrevButton>
				<Carousel
					selectedItem={currentImage}
					showArrows={false} // Hide default arrows
					showThumbs={false}
					stopOnHover
					// NOTE: no dynamicHeight — its height probe picks up the
					// PRE-transform slide height, cropping 90/270-rotated
					// images to square. The frame is a fixed 80vh column
					// (see CarouselContent CSS) and slides center inside it.
					showStatus={false} // Hide "1 of N" status, we show it in legend
					showIndicators={false} // Hide dot indicators again
					onChange={handleCarouselChange} // Use custom handler that detects wrap-around for page transitions
					infiniteLoop={true} // Enable infinite loop so we can detect swipe past edges
					// Prevent Carousel from stealing focus on render/change
					autoFocus={false}
				>
					{/* Map over fullImages state from parent */}
					{fullImages.map((img, index) => {
						// Safeguard: Ensure the corresponding mediaItem exists
						const mediaItem = mediaItems[index];
						if (!mediaItem) {
							// Render a loading state or null if mediaItem is missing
							return (
								<LoadingMessage key={index}>
									<LoadingGif size="large" />
								</LoadingMessage>
							);
						}

						// If the full image data hasn't loaded yet, show placeholder/loading
						if (!img) {
							return (
								<LoadingMessage key={index}>
									<LoadingGif size="large" />
								</LoadingMessage>
							);
						}

						// Render image or video placeholder
						// Lossless display rotation (never modifies image bytes):
						// for 90/270 we compute a swapped bounding box and rotate
						// the image inside it so it fits without overflow.
						const rotation = mediaItem.rotation ?? 0;
						const isRotated90 =
							rotation === 90 || rotation === 270;
						let imageWrapStyle: React.CSSProperties | undefined;
						let imageStyle: React.CSSProperties | undefined;
						if (rotation && mediaItem.width && mediaItem.height) {
							if (isRotated90) {
								const maxW = window.innerWidth * 0.8;
								const maxH = window.innerHeight * 0.8;
								const effW = mediaItem.height;
								const effH = mediaItem.width;
								const fitScale = Math.min(
									1,
									maxW / effW,
									maxH / effH
								);
								const boxW = Math.round(effW * fitScale);
								const boxH = Math.round(effH * fitScale);
								imageWrapStyle = {
									width: boxW,
									height: boxH,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									overflow: 'hidden',
									margin: '0 auto',
								};
								imageStyle = {
									width: boxH,
									height: boxW,
									maxWidth: 'none',
									maxHeight: 'none',
									transform: `rotate(${rotation}deg)`,
								};
							} else {
								imageStyle = {
									transform: `rotate(${rotation}deg)`,
								};
							}
						}
						return (
							<div key={mediaItem._id || index}>
								<S.MediaContainer>
									{mediaItem.type === 'video' ? (
										<S.VideoPlaceholder
											onClick={(e) => e.stopPropagation()}
											href={mediaItem.path}
											target="_blank"
											rel="noopener noreferrer"
											title="Obrir vídeo en una nova pestanya"
											$thumbnailUrl={
												mediaItem.thumbnail_path
											}
											$aspectRatio={
												mediaItem.thumbnail_height
													? mediaItem.thumbnail_width /
													  mediaItem.thumbnail_height
													: 16 / 9
											}
										>
											<span>
												Fes clic per obrir el vídeo en
												una nova pestanya
											</span>
										</S.VideoPlaceholder>
									) : (
										<div
											style={imageWrapStyle}
											onClick={(e) => e.stopPropagation()}
										>
											<img
												src={img.src} // Use loaded full image src
												alt={`${mediaItem.type} ${
													index + 1
												}`}
												style={imageStyle}
											/>
										</div>
									)}
								</S.MediaContainer>
							</div>
						);
					})}
				</Carousel>
				<S.CustomNextButton
					onClick={(e) => {
						e.stopPropagation(); // Prevent backdrop close
						if (currentImage !== null)
							onChangeImage(currentImage + 1); // Use prop handler
					}}
					aria-label="Next Image"
				>
					{'>'}
				</S.CustomNextButton>
			</S.CarouselContent>
			{/* Render Legend Outside Carousel */}
			{currentImage !== null &&
				renderLegend(currentImage, mediaItems[currentImage])}

			{/* Download Modal for Images */}
			{showDownloadModal && currentImage !== null && mediaItems[currentImage] && (() => {
				const item = mediaItems[currentImage];
				return (
					<S.DownloadModalBackdrop onClick={() => setShowDownloadModal(false)}>
						<S.DownloadModalCard onClick={(e) => e.stopPropagation()}>
							<S.DownloadModalTitle>Qualitat</S.DownloadModalTitle>
							<S.DownloadRow onClick={() => handleVersionDownload('web')}>
								<S.DownloadLabel>Web</S.DownloadLabel>
								{item.file_size ? (
									<S.DownloadSize>
										{formatSizeCompact(item.file_size)}
									</S.DownloadSize>
								) : null}
							</S.DownloadRow>
							<S.DownloadRow onClick={() => handleVersionDownload('original')}>
								<S.DownloadLabel>Original</S.DownloadLabel>
								{item.original_file_size ? (
									<S.DownloadSize>
										{formatSizeCompact(
											item.original_file_size
										)}
									</S.DownloadSize>
								) : null}
							</S.DownloadRow>
						</S.DownloadModalCard>
					</S.DownloadModalBackdrop>
				);
			})()}
		</S.CarouselBackdrop>
	);
};

export default MediaLightbox;
